import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { CollapsibleSection } from "./CollapsibleSection";
import { isSafeExportHref } from "./tableExport";
import type { Citation } from "./types";

const Wrapper = styled.div`
  margin-top: ${({ theme }) => theme.resolve("spacing_lg")};
  padding-top: ${({ theme }) => theme.resolve("spacing_md")};
  border-top: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.secondaryBorder};
`;
const Search = styled.input`
  box-sizing: border-box;
  width: 100%;
  font: inherit;
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  padding: ${({ theme }) => theme.resolve("spacing_md")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_sm")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.primaryBorder};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  &:focus-visible {
    outline: ${({ theme }) => theme.resolve("border_2xl")} solid
      ${({ theme }) => theme.brandColor};
    outline-offset: 1px;
  }
`;
const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 240px;
  overflow-y: auto;
  overscroll-behavior: contain;
  & li {
    padding: ${({ theme }) => theme.resolve("spacing_sm")} 0;
  }
  & a {
    color: ${({ theme }) => theme.brandColor};
    text-decoration: underline;
    overflow-wrap: anywhere;
  }
  & small {
    display: block;
    color: ${({ theme }) => theme.secondaryTextColor};
    font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  }
`;
const typeLabels: Record<string, string> = {
  product: "Product",
  document: "Document",
  asset: "Document",
  supplier: "Supplier",
  entity: "Record",
  sku: "SKU",
};
export function Citations({ citations }: { citations: Citation[] }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  if (!citations?.length) return null;
  const filtered = citations.filter((c) =>
    `${c.name || ""} ${c.product_number || ""} ${c.id} ${t(
      typeLabels[c.object_type] || "Reference"
    )}`
      .toLocaleLowerCase()
      .includes(query.trim().toLocaleLowerCase())
  );
  return (
    <Wrapper>
      <CollapsibleSection
        label={`${t("References")} · ${citations.length}`}
        collapseLabel={t("Collapse")}
        expandLabel={t("Expand")}
      >
        {citations.length >= 10 && (
          <Search
            type="search"
            aria-label={t("Filter references")}
            placeholder={t("Find a reference…")}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        )}
        {query && (
          <small role="status">
            {filtered.length} / {citations.length}
          </small>
        )}
        <List aria-label={t("References")} tabIndex={0}>
          {filtered.map((c, index) => {
            const label = c.name || c.product_number || c.id;
            const safe = isSafeExportHref(c.url || undefined);
            return (
              <li key={`${c.id}-${index}`}>
                {safe ? (
                  <a
                    href={c.url!}
                    {...(/^https?:\/\//i.test(c.url!)
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                  >
                    {label}
                  </a>
                ) : (
                  <span>{label}</span>
                )}
                <small>
                  {t(typeLabels[c.object_type] || "Reference")}
                  {c.product_number && c.product_number !== label
                    ? ` · ${c.product_number}`
                    : ""}
                </small>
              </li>
            );
          })}
        </List>
        {filtered.length === 0 && <p>{t("No matching references.")}</p>}
      </CollapsibleSection>
    </Wrapper>
  );
}
